import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";

import StartScreen from "./steps/StartScreen";
import SituationStep from "./steps/SituationStep";
import FFFFStep from "./steps/FFFFStep";
import PatternStep from "./steps/PatternStep";
import CostStep from "./steps/CostStep";
import NextMoveStep from "./steps/NextMoveStep";
import MessageBuilderStep from "./steps/MessageBuilderStep";
import ReceiptStep from "./steps/ReceiptStep";
import KeshaMark from "./KeshaMark";

const STORAGE_KEY = "ks_clarity_check_v1";
const TOTAL_STEPS = 8; // step 1..8 shown in progress; "0" is the start screen

const emptyState = {
  step: 0,
  // Step 1
  s1_whatHappened: "",
  s1_saidOrDone: "",
  s1_addingAssuming: "",
  // Step 2
  s2_facts: "",
  s2_feelings: "",
  s2_fear: "",
  s2_fantasy: "",
  // Step 3
  s3_felt_before: "", // "yes" | "no" | "unsure"
  s3_repeating: "",
  // Step 4
  s4_costs: [],
  s4_other: "",
  // Step 5
  s5_moves: [],
  s5_nextMove: "",
  s5_willNotDo: "",
  s5_worse: "",
  // Step 6 - Message Builder
  s6_short_a: "",
  s6_short_b: "",
  s6_clear_a: "",
  s6_clear_b: "",
  s6_clear_c: "",
  s6_skipped: false,
};

function loadState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...emptyState };
    const parsed = JSON.parse(raw);
    return { ...emptyState, ...parsed };
  } catch {
    return { ...emptyState };
  }
}

export default function ClarityCheck() {
  const [state, setState] = useState(() => loadState());
  const [confirmNewOpen, setConfirmNewOpen] = useState(false);
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);
  const skipNextSaveRef = useRef(false);

  // Autosave to localStorage on every change (debounced to next tick)
  useEffect(() => {
    if (skipNextSaveRef.current) {
      skipNextSaveRef.current = false;
      return;
    }
    const id = setTimeout(() => {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      } catch {
        /* quota errors ignored */
      }
    }, 120);
    return () => clearTimeout(id);
  }, [state]);

  // Scroll to top on step change for clean mobile feel
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [state.step]);

  const update = useCallback((patch) => {
    setState((s) => ({ ...s, ...patch }));
  }, []);

  const goTo = useCallback((step) => update({ step }), [update]);
  const next = useCallback(() => update({ step: Math.min(state.step + 1, TOTAL_STEPS) }), [state.step, update]);
  const back = useCallback(() => update({ step: Math.max(state.step - 1, 0) }), [state.step, update]);

  const startNewCheck = () => {
    setState({ ...emptyState, step: 1 });
    toast("New check started. Previous answers cleared.");
  };

  const clearSaved = () => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
    skipNextSaveRef.current = true;
    setState({ ...emptyState });
    toast("Saved answers cleared from this device.");
  };

  const progressPct = useMemo(() => {
    if (state.step === 0) return 0;
    return Math.round(((state.step) / TOTAL_STEPS) * 100);
  }, [state.step]);

  const stepTitle = [
    "",
    "Situation Snapshot",
    "Facts, Feelings, Fear & Fantasy",
    "Pattern Check",
    "Cost Check",
    "Pick One Next Move",
    "Message Builder",
    "Clarity Receipt",
  ][state.step] || "";

  return (
    <div className="relative z-10 min-h-screen">
      {/* Header — shown on all screens except the printable receipt section is separate */}
      <header className="print-hide sticky top-0 z-20 backdrop-blur-md bg-[rgba(10,10,11,0.78)] border-b ks-hair">
        <div className="max-w-[720px] mx-auto px-5 pt-4 pb-3 flex items-center justify-between gap-4">
          <button
            onClick={() => goTo(0)}
            className="flex items-center gap-3"
            data-testid="header-logo-btn"
            aria-label="Home"
          >
            <KeshaMark className="h-8 w-auto" />
          </button>

          {state.step > 0 && (
            <div className="flex items-center gap-3 min-w-0">
              <span
                className="font-elite uppercase text-[12px] tracking-[0.08em] text-[color:var(--ks-ink-soft)]"
                data-testid="step-indicator"
              >
                Step {state.step} of {TOTAL_STEPS}
              </span>
            </div>
          )}
        </div>

        {state.step > 0 && (
          <div className="max-w-[720px] mx-auto px-5 pb-3">
            <div className="ks-progress-track" role="progressbar" aria-valuenow={progressPct} aria-valuemin={0} aria-valuemax={100}>
              <div className="ks-progress-fill" style={{ width: `${progressPct}%` }} />
            </div>
          </div>
        )}
      </header>

      <main className="max-w-[720px] mx-auto px-5 pt-8 pb-28 print-hide">
        {state.step === 0 && (
          <StartScreen
            onStart={() => goTo(1)}
            onResume={() => goTo(Math.max(state.step, 1))}
            hasProgress={hasProgress(state)}
            onRequestClear={() => setConfirmClearOpen(true)}
          />
        )}

        {state.step >= 1 && state.step <= 7 && (
          <StepShell title={stepTitle} stepNumber={state.step}>
            {state.step === 1 && <SituationStep state={state} update={update} />}
            {state.step === 2 && <FFFFStep state={state} update={update} />}
            {state.step === 3 && <PatternStep state={state} update={update} />}
            {state.step === 4 && <CostStep state={state} update={update} />}
            {state.step === 5 && <NextMoveStep state={state} update={update} />}
            {state.step === 6 && (
              <MessageBuilderStep
                state={state}
                update={update}
                onSkip={() => {
                  update({ s6_skipped: true });
                  next();
                }}
              />
            )}
            {state.step === 7 && (
              <PreReceiptStep onView={next} />
            )}
          </StepShell>
        )}

        {state.step === 8 && (
          <ReceiptStep
            state={state}
            onBack={back}
            onNewCheck={() => setConfirmNewOpen(true)}
            onClearSaved={() => setConfirmClearOpen(true)}
          />
        )}

        {state.step >= 1 && state.step <= 7 && (
          <NavBar
            step={state.step}
            onBack={back}
            onNext={next}
            showSkip={state.step === 6}
            onSkip={() => {
              update({ s6_skipped: true });
              next();
            }}
          />
        )}
      </main>

      {/* PRINTABLE AREA — rendered off-screen, becomes only thing printed */}
      <PrintableReceipt state={state} />

      {/* Confirmation dialogs */}
      <AlertDialog open={confirmNewOpen} onOpenChange={setConfirmNewOpen}>
        <AlertDialogContent className="bg-[#121214] border-[rgba(245,242,236,0.18)] text-[#f5f2ec]">
          <AlertDialogHeader>
            <AlertDialogTitle className="ks-h3">Start a new check?</AlertDialogTitle>
            <AlertDialogDescription className="text-[color:var(--ks-ink-soft)]" style={{ fontFamily: "Georgia, serif" }}>
              This clears the answers you just wrote. You cannot undo it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="ks-btn ks-btn-ghost"
              data-testid="confirm-new-cancel"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="ks-btn ks-btn-primary"
              data-testid="confirm-new-ok"
              onClick={startNewCheck}
            >
              Yes, start new
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmClearOpen} onOpenChange={setConfirmClearOpen}>
        <AlertDialogContent className="bg-[#121214] border-[rgba(245,242,236,0.18)] text-[#f5f2ec]">
          <AlertDialogHeader>
            <AlertDialogTitle className="ks-h3">Clear saved answers?</AlertDialogTitle>
            <AlertDialogDescription className="text-[color:var(--ks-ink-soft)]" style={{ fontFamily: "Georgia, serif" }}>
              Everything you wrote on this device will be deleted. This is permanent.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="ks-btn ks-btn-ghost"
              data-testid="confirm-clear-cancel"
            >
              Keep my answers
            </AlertDialogCancel>
            <AlertDialogAction
              className="ks-btn ks-btn-danger"
              data-testid="confirm-clear-ok"
              onClick={clearSaved}
            >
              Yes, clear everything
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function hasProgress(state) {
  return Object.entries(state).some(([k, v]) => {
    if (k === "step" || k === "s6_skipped") return false;
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === "string") return v.trim().length > 0;
    return false;
  });
}

/* ---------- Layout helpers ---------- */

function StepShell({ title, stepNumber, children }) {
  return (
    <section className="ks-rise">
      <p className="ks-kicker mb-3">Step {stepNumber}</p>
      <h1 className="ks-h2 mb-8 leading-[1.08]">{title}</h1>
      <div className="space-y-8">{children}</div>
    </section>
  );
}

function PreReceiptStep({ onView }) {
  return (
    <div className="ks-rise space-y-8">
      <p className="ks-kicker">Almost done</p>
      <h2 className="ks-h2">Ready to see the receipt?</h2>
      <p className="text-[17px] leading-[1.6] text-[color:var(--ks-ink-soft)]">
        We will pull your answers into one clean page you can copy, print, or save as a PDF. Nothing gets sent anywhere.
      </p>
      <button
        className="ks-btn ks-btn-primary w-full sm:w-auto"
        onClick={onView}
        data-testid="view-receipt-btn"
      >
        View my Clarity Receipt
      </button>
    </div>
  );
}

function NavBar({ step, onBack, onNext, showSkip, onSkip }) {
  return (
    <nav className="print-hide mt-12 pt-6 border-t ks-hair flex flex-wrap items-center gap-3">
      <button
        className="ks-btn ks-btn-ghost"
        onClick={onBack}
        data-testid="nav-back-btn"
      >
        ← Back
      </button>
      {showSkip && (
        <button
          className="ks-btn ks-btn-ghost"
          onClick={onSkip}
          data-testid="nav-skip-btn"
        >
          Skip this step
        </button>
      )}
      <div className="ml-auto">
        <button
          className="ks-btn ks-btn-primary"
          onClick={onNext}
          data-testid="nav-next-btn"
        >
          {step === 7 ? "See receipt" : "Next"} →
        </button>
      </div>
    </nav>
  );
}

/* ---------- Printable Receipt (always mounted, invisible on screen) ---------- */

function PrintableReceipt({ state }) {
  const lines = buildReceiptLines(state);
  return (
    <div
      className="print-receipt"
      aria-hidden="true"
      style={{
        position: "absolute",
        left: "-10000px",
        top: 0,
        width: "720px",
        background: "#fff",
        color: "#0a0a0b",
        padding: "24px",
        fontFamily: "Georgia, 'Times New Roman', serif",
      }}
    >
      <div style={{ borderBottom: "1px solid #d9d5cd", paddingBottom: 12, marginBottom: 20 }}>
        <div className="font-elite print-accent" style={{ fontSize: 11, letterSpacing: "0.24em", textTransform: "uppercase", color: "#ff003d" }}>
          Kesha Speaks
        </div>
        <h1 style={{ fontSize: 34, margin: "6px 0 4px", lineHeight: 1.05 }}>
          The “Am I Tripping?” Clarity Check
        </h1>
        <div style={{ fontSize: 13, color: "#4a4a4a" }}>
          Receipt generated {formatDate(new Date())}
        </div>
      </div>
      {lines.map((block, i) => (
        <ReceiptBlock key={i} title={block.title} items={block.items} forPrint />
      ))}
      <div style={{ marginTop: 28, fontSize: 12, color: "#4a4a4a", borderTop: "1px solid #d9d5cd", paddingTop: 12 }}>
        Your answers are saved on this device only. Kesha Speaks does not receive or review your entries.
      </div>
    </div>
  );
}

export function ReceiptBlock({ title, items, forPrint }) {
  if (!items || items.length === 0) return null;
  return (
    <section style={{ marginBottom: forPrint ? 22 : undefined }}
      className={forPrint ? "" : "pb-6 mb-6 border-b ks-hair"}>
      <h3
        className={forPrint ? "" : "ks-h3 mb-3"}
        style={forPrint ? { fontSize: 18, margin: "0 0 8px", color: "#0a0a0b" } : undefined}
      >
        {title}
      </h3>
      <dl style={forPrint ? { margin: 0 } : undefined} className={forPrint ? "" : "space-y-3"}>
        {items.map((it, idx) => (
          <div key={idx} style={forPrint ? { marginBottom: 10 } : undefined}>
            {it.label && (
              <dt
                className={forPrint ? "" : "ks-kicker"}
                style={forPrint ? { fontFamily: "'Special Elite', monospace", fontSize: 10, letterSpacing: "0.22em", textTransform: "uppercase", color: "#6f6a63", marginBottom: 3 } : { marginBottom: 4 }}
              >
                {it.label}
              </dt>
            )}
            <dd
              style={forPrint
                ? { margin: 0, fontSize: 15, lineHeight: 1.55, color: "#0a0a0b", whiteSpace: "pre-wrap" }
                : { whiteSpace: "pre-wrap" }}
              className={forPrint ? "" : "text-[16px] leading-[1.55] text-[color:var(--ks-ink)]"}
            >
              {it.value}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

export function buildReceiptLines(state) {
  const emptyMark = "—";
  const t = (v) => (v && v.trim() ? v.trim() : emptyMark);
  const arr = (a) => (a && a.length ? a.join(", ") : emptyMark);
  const felt = { yes: "Yes", no: "No", unsure: "Not sure, but my body is side-eyeing this" }[state.s3_felt_before] || emptyMark;

  const shortVersion = composeShort(state);
  const clearVersion = composeClear(state);
  const noMore = "I have already explained this. I am not going to keep restating it.";

  const blocks = [
    {
      title: "Situation Snapshot",
      items: [
        { label: "What happened", value: t(state.s1_whatHappened) },
        { label: "What was actually said or done", value: t(state.s1_saidOrDone) },
        { label: "What I'm adding, assuming, or interpreting", value: t(state.s1_addingAssuming) },
      ],
    },
    {
      title: "Facts, Feelings, Fear & Fantasy",
      items: [
        { label: "Facts", value: t(state.s2_facts) },
        { label: "Feelings", value: t(state.s2_feelings) },
        { label: "Fear", value: t(state.s2_fear) },
        { label: "Fantasy", value: t(state.s2_fantasy) },
      ],
    },
    {
      title: "Pattern Check",
      items: [
        { label: "Have I felt this way with this person before?", value: felt },
        { label: "What keeps repeating", value: t(state.s3_repeating) },
      ],
    },
    {
      title: "Cost Check",
      items: [
        { label: "What this is costing me", value: arr([...state.s4_costs, ...(state.s4_other.trim() ? [`Other: ${state.s4_other.trim()}`] : [])]) },
      ],
    },
    {
      title: "Pick One Next Move",
      items: [
        { label: "Options I'm considering", value: arr(state.s5_moves) },
        { label: "My next move", value: t(state.s5_nextMove) },
        { label: "What I will not do right now", value: t(state.s5_willNotDo) },
        { label: "What would make this worse", value: t(state.s5_worse) },
      ],
    },
  ];

  if (!state.s6_skipped) {
    blocks.push({
      title: "Message Builder",
      items: [
        { label: "The short version", value: shortVersion },
        { label: "The clear version", value: clearVersion },
        { label: "The no-more-paragraphs version", value: noMore },
      ],
    });
  }

  return blocks;
}

function composeShort(state) {
  const a = state.s6_short_a.trim() || "________";
  const b = state.s6_short_b.trim() || "________";
  return `I am not available for ${a}. I am going to ${b}.`;
}

function composeClear(state) {
  const a = state.s6_clear_a.trim() || "________";
  const b = state.s6_clear_b.trim() || "________";
  const c = state.s6_clear_c.trim() || "________";
  return `When ${a} happened, I felt ${b}. What I need moving forward is ${c}.`;
}

export { composeShort, composeClear };

function formatDate(d) {
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const hh = d.getHours().toString().padStart(2, "0");
  const mm = d.getMinutes().toString().padStart(2, "0");
  return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()} · ${hh}:${mm}`;
}
